import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import apiClient from '../client';
import {
  UserRegistrationInput,
  UserRegistrationView,
  UserPhoneVerificationRequestInput,
  UserPhoneVerificationConfirmInput,
  ChangePasswordInput,
  PasswordResetRequestInput,
  PasswordResetConfirmInput,
  UserDeletionRequestInput,
  LoginCredentials,
  AuthTokens,
  ApiError,
} from '../types';

export const useLogin = (
  options?: UseMutationOptions<AuthTokens, ApiError, LoginCredentials>,
) => {
  return useMutation<AuthTokens, ApiError, LoginCredentials>({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await apiClient.post<AuthTokens>(
        '/login_check',
        credentials,
      );

      await apiClient.setAuthToken(
        response.data.token,
        response.data.refreshToken,
      );

      return response.data;
    },
    ...options,
  });
};

export const useLogout = (
  options?: UseMutationOptions<void, ApiError, void>,
) => {
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await apiClient.clearAuth();
    },
    ...options,
  });
};

export const useRegister = (
  options?: UseMutationOptions<
    UserRegistrationView,
    ApiError,
    UserRegistrationInput
  >,
) => {
  return useMutation<UserRegistrationView, ApiError, UserRegistrationInput>({
    mutationFn: async (data: UserRegistrationInput) => {
      const response = await apiClient.post<UserRegistrationView>(
        '/users/register',
        data,
      );
      return response.data;
    },
    ...options,
  });
};

export const useRequestPhoneVerification = (
  options?: UseMutationOptions<
    void,
    ApiError,
    UserPhoneVerificationRequestInput
  >,
) => {
  return useMutation<void, ApiError, UserPhoneVerificationRequestInput>({
    mutationFn: async (data: UserPhoneVerificationRequestInput) => {
      await apiClient.post('/users/register/phone', data);
    },
    ...options,
  });
};

export const useConfirmPhoneVerification = (
  options?: UseMutationOptions<
    void,
    ApiError,
    UserPhoneVerificationConfirmInput
  >,
) => {
  return useMutation<void, ApiError, UserPhoneVerificationConfirmInput>({
    mutationFn: async (data: UserPhoneVerificationConfirmInput) => {
      await apiClient.post('/users/register/phone/confirm', data);
    },
    ...options,
  });
};

export const useChangePassword = (
  options?: UseMutationOptions<void, ApiError, ChangePasswordInput>,
) => {
  return useMutation<void, ApiError, ChangePasswordInput>({
    mutationFn: async (data: ChangePasswordInput) => {
      await apiClient.post('/users/change-password', data);
    },
    ...options,
  });
};

export const useRequestPasswordReset = (
  options?: UseMutationOptions<void, ApiError, PasswordResetRequestInput>,
) => {
  return useMutation<void, ApiError, PasswordResetRequestInput>({
    mutationFn: async (data: PasswordResetRequestInput) => {
      await apiClient.post('/users/password-reset', data);
    },
    ...options,
  });
};

export const useConfirmPasswordReset = (
  options?: UseMutationOptions<void, ApiError, PasswordResetConfirmInput>,
) => {
  return useMutation<void, ApiError, PasswordResetConfirmInput>({
    mutationFn: async (data: PasswordResetConfirmInput) => {
      await apiClient.post('/users/password-reset/confirm', data);
    },
    ...options,
  });
};

export const useDeleteAccount = (
  options?: UseMutationOptions<void, ApiError, void>,
) => {
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await apiClient.delete('/users/me');
      await apiClient.clearAuth();
    },
    ...options,
  });
};

export const useRequestDeletion = (
  options?: UseMutationOptions<void, ApiError, UserDeletionRequestInput>,
) => {
  return useMutation<void, ApiError, UserDeletionRequestInput>({
    mutationFn: async (data: UserDeletionRequestInput) => {
      await apiClient.post('/users/me/deletion-request', data);
    },
    ...options,
  });
};
